import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';

interface ListInfiniteScrollProps {
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetching: boolean;
}

export function ListInfiniteScroll({
  hasNextPage,
  fetchNextPage,
  isFetching,
}: ListInfiniteScrollProps) {
  const { t } = useTranslation('listView');
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetching) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetching, fetchNextPage]);

  if (!hasNextPage && !isFetching) return null;

  return (
    <div ref={sentinelRef} className="flex items-center justify-center py-4 border-t border-border">
      {isFetching ? (
        <span className="text-sm text-muted-foreground">{t('infinite.loading')}</span>
      ) : (
        <Button variant="outline" size="sm" onClick={fetchNextPage} disabled={isFetching}>
          {t('infinite.loadMore')}
        </Button>
      )}
    </div>
  );
}
